import { MARKETS, capitalise, t } from './markets'
import { AIRCON_SERVICES, GUIDES, PT_TOPICS, marketKey } from '../data/markets/registry'

// Segment menus for the UAE / Malaysia aircon and personal trainer pages - the
// counterpart of the Singapore AirconNavBar / TrainerNavBar lists, built from
// data/markets/registry.js so a page added there shows up in the nav too.
//
// A menu is a list of entries, rendered by SegmentNavBar (desktop) and
// SegmentNavBarSmall (mobile) in components/index/navbars:
//   { type: 'dropdown', label, items: [{ href, label }] }
//   { type: 'link', href, label, ariaLabel }
//   { type: 'whatsapp', text, ariaLabel, label }   label: no +65 number on
//     the UAE / Malaysia pages - the WhatsApp line is the Singapore one

// Symptom pages ("not cooling", "leaking") go under Problems, not Services.
const isProblem = (slug) => /not-cool|not-cold|leak/.test(slug)
const isPricePage = (slug) => /price|rates/.test(slug)

const link = (base, entry) => ({ href: `${base}/${entry.slug}`, label: entry.title })

const airconMenu = (market, city) => {
    const base = market.hubs.aircon[city.slug]
    const key = marketKey(market.countryCd, city.slug)
    const pages = AIRCON_SERVICES[key] ?? []
    const guides = GUIDES[`${key}/aircon`] ?? []
    const term = capitalise(t(market, 'acShort'))
    const price = pages.find((p) => isPricePage(p.slug))
    const problems = pages.filter((p) => isProblem(p.slug))

    return [
        { type: 'dropdown', label: `${term} Services`, items: [
            { href: base, label: `${t(market, 'acCategory')} ${city.name}` },
            ...pages.filter((p) => !isProblem(p.slug) && !isPricePage(p.slug)).map((p) => link(base, p)),
        ] },
        price && { type: 'link', href: `${base}/${price.slug}`, label: 'Pricing', ariaLabel: price.title },
        problems.length > 0 && { type: 'dropdown', label: `${term} Problems`, items: problems.map((p) => link(base, p)) },
        guides.length > 0 && { type: 'dropdown', label: 'Guides', items: guides.map((g) => link(base, g)) },
        { type: 'whatsapp', label: 'WhatsApp', text: `Hi, I'd like to book ${t(market, 'acCategory').toLowerCase()} in ${city.name}.`, ariaLabel: `WhatsApp StringsSG to book ${t(market, 'acCategory').toLowerCase()}` },
    ].filter(Boolean)
}

const ptMenu = (market, city) => {
    const base = market.hubs.pt[city.slug]
    const key = marketKey(market.countryCd, city.slug)
    const topics = PT_TOPICS[key] ?? []
    const guides = GUIDES[`${key}/pt`] ?? []
    const rates = topics.find((p) => isPricePage(p.slug))
    const careers = guides.find((g) => g.slug.startsWith('become-a-personal-trainer'))

    return [
        { type: 'dropdown', label: 'Trainers', items: [
            { href: base, label: `Personal Trainer ${city.name}` },
            ...topics.filter((p) => !isPricePage(p.slug)).map((p) => link(base, p)),
        ] },
        rates && { type: 'link', href: `${base}/${rates.slug}`, label: 'Rates', ariaLabel: rates.title },
        guides.filter((g) => g !== careers).length > 0 && { type: 'dropdown', label: 'Guides', items: guides.filter((g) => g !== careers).map((g) => link(base, g)) },
        careers && { type: 'link', href: `${base}/${careers.slug}`, label: 'Careers', ariaLabel: careers.title },
        { type: 'whatsapp', label: 'WhatsApp', text: `Hi, I'd like to book a personal trainer in ${city.name}.`, ariaLabel: 'WhatsApp StringsSG to book a personal trainer' },
    ].filter(Boolean)
}

// The segment menu for a UAE / Malaysia route, or null (Singapore and other
// routes keep IndexNav's own segment navbars). Matches the route against the
// market's hub paths, so hubs, service pages, guides and locality pages all
// get their city's menu.
export const getMarketSegmentMenu = (pathname) => {
    const parts = (pathname || '').split('/').filter(Boolean)
    const market = MARKETS[parts[0]]
    if (!market || market.countryCd === 'sg' || parts.length < 2) return null
    const hubSegment = `/${parts[0]}/${parts[1]}`
    for (const city of market.cities) {
        if (market.hubs.aircon[city.slug] === hubSegment) return { vertical: 'aircon', items: airconMenu(market, city) }
        if (market.hubs.pt[city.slug] === hubSegment) return { vertical: 'pt', items: ptMenu(market, city) }
    }
    return null
}
